import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import isString from 'lodash/isString';
import last from 'lodash/last';
import { isStringTypes, isNumberTypes, quotes, END, TYPE_OP_MAPPING } from './queryExpression.esm.js';
import format from 'date-fns/format';
import isDate from 'date-fns/isDate';
import toDate from 'date-fns/toDate';
import isValid from 'date-fns/isValid';
import parseISO from 'date-fns/parseISO';
import { isNil, isNilOrEmptyString, isTypeTimestamp } from './utils.esm.js';

const EMPTY_STR = {
    name: "''",
    moveCursor: 1,
};
function getOperators(tokenOrExp) {
    return get(tokenOrExp, "field.operators", TYPE_OP_MAPPING[""]);
}
function filterSuggestions(suggestions, cursor, current, sortLimit = 0) {
    if (isNil(suggestions) || (current.from && cursor <= current.from)) {
        return suggestions;
    }
    let matched = cursor - (current.from ? current.from : 0);
    if (!isNil(current.quoted) || /^['"`]/.test(current.value)) {
        matched -= 1;
    }
    const sliced = current.value.slice(0, matched).toLowerCase();
    const filterFn = (f) => (f.name || f)
        .toLowerCase()
        .startsWith(sliced);
    const matchedFn = 
    // Cant find use case
    // Matched
    (s) => ({
        ...(s.name
            ? s
            : { name: s }),
        matched,
    });
    if (isNil(sortLimit) || suggestions.length > sortLimit) {
        return suggestions.filter(filterFn).map(matchedFn);
    }
    const { true: matchedGroup, false: unmatchedGroup = [] } = groupBy(suggestions, filterFn);
    return !isNil(matchedGroup)
        ? [...matchedGroup.map(matchedFn), ...unmatchedGroup]
        : suggestions;
}
function isPartOfAndOr(current) {
    return (isNil(current.kind) &&
        !isNil(current.value) &&
        "AN\x00O".includes(current.value.toUpperCase()));
}
function formatTimestamp(timestamp, timeFormat) {
    let date = timestamp;
    if (isDate(timestamp)) {
        date = timestamp;
    }
    else if (isString(timestamp)) {
        if (isValid(parseISO(timestamp))) {
            date = parseISO(timestamp);
        }
        else {
            return timestamp;
        }
    }
    else if (isValid(timestamp)) {
        date = toDate(timestamp);
    }
    return format(date, timeFormat);
}
function genEmptyTimestamp() {
    return {
        name: "''",
        moveCursor: 1,
    };
}
function getFieldEnum(fields, name) {
    var _a;
    const field = Array.isArray(fields) ? name : fields;
    if (isNil(fields) ||
        isNil(field.enum) ||
        !Array.isArray(field.enum)) {
        return undefined;
    }
    const quoted = field.type === "enum" ? "'" : null;
    // Convert
    return (_a = field.enum) === null || _a === void 0 ? void 0 : _a.map((value) => {
        return {
            name: value,
            quoted,
        };
    });
}
function getSuggestion(fields, supportNot, value, timeFormat, cursor, queryExp) {
    var _a;
    let suggestions = [];
    const fieldsWithNot = supportNot
        ? [...fields, "NOT"]
        : [...fields];
    if (queryExp.query !== "" || queryExp.tokens.length > 0) {
        const idx = queryExp.tokens.findIndex((t) => cursor >= t.from && cursor <= t.to);
        let current = queryExp.tokens[idx];
        if (!isNil(current) && current.kind === "paren") {
            if (current.to === cursor && current.value === "(") {
                current = queryExp.tokens[idx + 1];
            }
            else {
                current = undefined;
            }
        }
        const lastToken = last(queryExp.tokens);
        if (!isNil(current)) {
            if ((current === null || current === void 0 ? void 0 : current.kind) === "key") {
                suggestions = filterSuggestions(fieldsWithNot, cursor, current);
            }
            else if ((current === null || current === void 0 ? void 0 : current.kind) === "op") {
                const operators = getOperators(current);
                suggestions = filterSuggestions(operators, cursor, current, 3);
            }
            else if ((current === null || current === void 0 ? void 0 : current.kind) === "not" && supportNot) {
                suggestions = ["NOT"];
            }
            else if ((current === null || current === void 0 ? void 0 : current.kind) === "value") {
                const field = current.field;
                const enums = getFieldEnum(field);
                const type = isNil(field) ? null : field === null || field === void 0 ? void 0 : field.type;
                if (!isNil(enums)) {
                    suggestions = filterSuggestions(enums, cursor, current, 3);
                }
                else if (isStringTypes(type)) {
                    if (((_a = current.exp) === null || _a === void 0 ? void 0 : _a.op.toUpperCase()) === "LIKE") {
                        suggestions = [
                            {
                                name: quotes(current.value),
                            },
                        ];
                    }
                    else {
                        suggestions = [
                            {
                                name: quotes(current.value),
                            },
                        ];
                    }
                    if (!isNilOrEmptyString(current.value)) {
                        suggestions.push(EMPTY_STR);
                    }
                }
                else if (isNumberTypes(type)) {
                    const int = isNilOrEmptyString(current.value) ? 0 : +current.value;
                    suggestions = [
                        {
                            name: String(int),
                        },
                    ];
                }
                else if (type === "boolean") {
                    suggestions = filterSuggestions([
                        {
                            name: "true",
                        },
                        {
                            name: "false",
                        },
                    ], cursor, current, 2);
                }
                else if (isTypeTimestamp(type)) {
                    let timestamp;
                    if (+current.value >= 0) {
                        timestamp = +current.value * 1000;
                    }
                    else {
                        timestamp = current.value.trim();
                    }
                    const now = new Date();
                    const currentTimeFormat = isNilOrEmptyString(field === null || field === void 0 ? void 0 : field.format)
                        ? timeFormat
                        : field === null || field === void 0 ? void 0 : field.format;
                    const date = new Date(timestamp);
                    if (+date > 0 && +date !== +now) {
                        suggestions.unshift({
                            name: quotes(formatTimestamp(timestamp, currentTimeFormat)),
                        });
                        suggestions.push(genEmptyTimestamp());
                    }
                    else {
                        suggestions.unshift(genEmptyTimestamp());
                    }
                }
                else {
                    let name;
                    if (!isNilOrEmptyString(current.quoted)) {
                        name = quotes(current.value);
                    }
                    else {
                        name = current.value;
                    }
                    suggestions = [
                        {
                            name,
                            disabled: true,
                        },
                    ];
                }
            }
            else if ((current === null || current === void 0 ? void 0 : current.kind) === "andor" ||
                isPartOfAndOr(current)) {
                // Could be part of and/or
                suggestions = filterSuggestions(["AND", "OR"], cursor, current, 2);
            }
            const appendSpace = lastToken === current ? " " : "";
            suggestions = suggestions.map((s) => {
                let name;
                if (isString(s)) {
                    name = s.trim();
                    const pre = (value.slice(0, current === null || current === void 0 ? void 0 : current.from) + name).trim();
                    const full = `${pre} ${value.slice(current === null || current === void 0 ? void 0 : current.to).trim()}`;
                    return {
                        name,
                        matched: null,
                        cursor: pre.length,
                        value: `${full.trim()}${appendSpace}`,
                        disabled: false,
                    };
                }
                else {
                    name = String(s.name).trim();
                    const matched = s.matched;
                    const pre = (value.slice(0, current === null || current === void 0 ? void 0 : current.from) + name).trim();
                    const full = `${pre} ${value.slice(current === null || current === void 0 ? void 0 : current.to).trim()}`;
                    const newValue = s.disabled === true ? "\n" : `${full.trim()}${appendSpace}`;
                    const cursor = s.moveCursor && s.moveCursor > 0
                        ? current.from + s.moveCursor
                        : s.moveCursor && s.moveCursor < 0
                            ? current.to - s.moveCursor
                            : appendSpace
                                ? newValue.length
                                : pre.length;
                    return {
                        name,
                        matched,
                        cursor,
                        value: newValue,
                        disabled: s.disabled,
                    };
                }
            });
        }
        else if (cursor >= get(lastToken, "to", value.length)) {
            if ((lastToken === null || lastToken === void 0 ? void 0 : lastToken.value) === "(") {
                suggestions = fieldsWithNot;
            }
            else {
                let q = queryExp.tree;
                while (!isNil(q) && !isNil(q === null || q === void 0 ? void 0 : q.args)) {
                    if ((q === null || q === void 0 ? void 0 : q.op) === END) {
                        suggestions = getOperators(q);
                        break;
                    }
                    const lastFind = last(q === null || q === void 0 ? void 0 : q.args);
                    if (isNil(lastFind)) {
                        suggestions = (lastToken === null || lastToken === void 0 ? void 0 : lastToken.kind) === "not" ? fields : fieldsWithNot;
                        break;
                    }
                    else if (lastFind === END) {
                        const field = q === null || q === void 0 ? void 0 : q.field;
                        if (isNil(field)) {
                            return [];
                        }
                        const enums = getFieldEnum(field);
                        const type = field === null || field === void 0 ? void 0 : field.type;
                        if (!isNil(field === null || field === void 0 ? void 0 : field.enum)) {
                            suggestions = enums;
                        }
                        else if (isStringTypes(type)) {
                            if ((q === null || q === void 0 ? void 0 : q.op.toUpperCase()) === "LIKE") {
                                suggestions = [
                                    // Disable for now, since ip does not support more than 1 star
                                    {
                                        name: "'%'",
                                        moveCursor: 1,
                                    },
                                    EMPTY_STR,
                                ];
                            }
                            else {
                                suggestions = [EMPTY_STR];
                            }
                        }
                        else if (isNumberTypes(type)) {
                            suggestions = [
                                {
                                    name: "0",
                                },
                            ];
                        }
                        else if (isTypeTimestamp(type)) {
                            suggestions = [
                                genEmptyTimestamp(),
                                {
                                    name: quotes(formatTimestamp(new Date(), (field === null || field === void 0 ? void 0 : field.format) || timeFormat)),
                                },
                            ];
                        }
                        else if (type === "boolean") {
                            suggestions = [
                                {
                                    name: "true",
                                },
                                {
                                    name: "false",
                                },
                            ];
                        }
                        else {
                            suggestions = [
                                {
                                    name: " ",
                                    disabled: true,
                                },
                            ];
                        }
                        break;
                    }
                    else {
                        q = lastFind;
                    }
                }
                if (q === null ||
                    q === undefined ||
                    q.args === null ||
                    q.args === undefined) {
                    suggestions = ["AND", "OR"]; // Append: and/or
                }
            }
            suggestions = suggestions.map((s) => {
                let name;
                if (isString(s)) {
                    name = s.trim();
                    return {
                        name,
                        matched: null,
                        cursor: -1,
                        value: `${value.trim()} ${name} `,
                        disabled: false,
                    };
                }
                else {
                    name = String(s.name).trim();
                    const matched = s.matched;
                    if (!isNil(s.quoted)) {
                        name = quotes(name);
                    }
                    const newValue = s.disabled === true ? "\n" : `${value.trim()} ${name} `;
                    const cursor = s.moveCursor && s.moveCursor > 0
                        ? value.length + s.moveCursor
                        : s.moveCursor && s.moveCursor < 0
                            ? newValue.length - s.moveCursor - 1
                            : -1;
                    return {
                        name,
                        value: newValue,
                        matched,
                        disabled: s.disabled,
                        cursor,
                    };
                }
            });
        }
    }
    else {
        suggestions = fields.map((f) => ({ ...f, value: `${f.name} ` }));
        suggestions = supportNot
            ? [...suggestions, "NOT"]
            : suggestions;
    }
    return suggestions;
}

export { formatTimestamp, getFieldEnum, getSuggestion };
//# sourceMappingURL=suggestionHelper.esm.js.map
